import React from "react";
import DebugDataComponent from "@/debug/DebugDataComponent";
import "./VacancyApplicantListEntry.scss";
import BFValueDisplay from "@/modules/abstract-ui/data/value-display/BFValueDisplay";
import i18n from "@/i18n";
import moment from "moment";
import VacancyApplicantEmployerPart from "./VacancyApplicantEmployerPart";
import { APOffer } from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import {
  VacancyApplicant,
  VacancyApplication,
} from "../../../model/VacancyApplicant.interface";
import VacancyApplicantCreditReportPart from "../credit-report/VacancyApplicantCreditReportPart";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import VacancyOfferUtils from "../../utils/VacancyOfferUtils";
import VacancyApplicantAdditionalPersonPart from "./VacancyApplicantAdditionalPersonPart";
import VacancyApplicantAttachmentPart from "./VacancyApplicantAttachmentPart";
import VacancyApplicantOtherInformation from "./VacancyApplicantOtherInformation";

type Props = {
  offer?: APOffer;
  activity: APActivity;
  constants: ActivityApplicationConstants;
  application: VacancyApplication;
  applicant: VacancyApplicant;
  renderGuarantor?: boolean;
  editable?: boolean;
  onOpenGuarantorModal?: () => void;
  onOpenAllContractPersonsModal?: () => void;
  closeModal?: () => void;
};

export const VacancyApplicantDetailedComponent = (props: Props) => {
  const {
    offer,
    activity,
    constants,
    application,
    applicant,
    renderGuarantor = false,
    editable = false,
    onOpenGuarantorModal,
    closeModal,
  } = props;

  const applicantData = renderGuarantor ? applicant.guarantor : applicant;

  const level = VacancyOfferUtils.getEmploymentStatusLevel(applicant, activity);

  return (
    <div className="vacancy-applicant-list-entry">
      {offer && <DebugDataComponent data={offer} />}
      <DebugDataComponent data={applicantData} />
      <div className="__h3">
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Label.GeneralInformation",
          "Allgemeine Informationen"
        )}
      </div>
      <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.MoveIn.Title",
            "Einzugstermin"
          )}
          value={
            <div>
              {moment(application.data.moveIn).format("DD.MM.YYYY") || "-"}
            </div>
          }
        />
      </div>
      <div className="__h3">
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Label.PersonalData",
          "Persönliche Daten"
        )}
      </div>
      <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.FirstName",
            "Vorname"
          )}
          value={applicantData?.firstName || "-"}
        />
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.LastName",
            "Nachname"
          )}
          value={applicantData?.lastName || "-"}
        />
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.DateOfBirth",
            "Geburtsdatum"
          )}
          value={moment(applicantData?.birthday).format("DD.MM.YYYY")}
        />
      </div>
      <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center padding-top-16">
        <BFValueDisplay
          label={i18n.t("apTemplate:Vacancy.Applicant.Label.Street", "Adresse")}
          value={`${applicantData?.street || "-"} ${
            applicantData?.streetNumber || "-"
          }`}
        />
        <BFValueDisplay
          label={i18n.t("apTemplate:Vacancy.Applicant.Label.ZipCode", "PLZ")}
          value={applicantData?.zipCode || "-"}
        />
        <BFValueDisplay
          label={i18n.t("apTemplate:Vacancy.Applicant.Label.City", "Ort")}
          value={applicantData?.city || "-"}
        />
      </div>
      <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.Telephone",
            "Telefon"
          )}
          value={applicantData?.phone || "-"}
        />
        <BFValueDisplay
          label={i18n.t("apTemplate:Vacancy.Applicant.Label.Email", "E-Mail")}
          value={applicantData?.email || "-"}
        />
        {applicantData?.contactNote && (
          <BFValueDisplay
            label={i18n.t(
              "apTemplate:Vacancy.Applicant.Label.Others",
              "Sonstiger Kontakt"
            )}
            value={applicantData.contactNote}
          />
        )}
        {!renderGuarantor && applicant?.note && (
          <BFValueDisplay
            label={i18n.t(
              "apTemplate:Vacancy.Applicant.Label.ApplicantNote",
              "Notiz"
            )}
            value={applicant.note}
          />
        )}
      </div>
      {!renderGuarantor && (
        <VacancyApplicantAdditionalPersonPart application={application} />
      )}
      <div className=" padding-top-20">
        <VacancyApplicantEmployerPart
          application={application}
          applicant={applicant}
          activity={activity}
          constants={constants}
          level={level}
          renderGuarantor={renderGuarantor}
          onOpenGuarantorModal={onOpenGuarantorModal}
          closeModal={closeModal}
        />
      </div>
      <div className="padding-top-20">
        <VacancyApplicantCreditReportPart
          application={application}
          applicant={applicant}
          constants={constants}
          noPadding
          editable={editable}
          renderGuarantor={renderGuarantor}
        />
      </div>
      {!renderGuarantor && (
        <VacancyApplicantOtherInformation applicant={applicant} />
      )}
      <VacancyApplicantAttachmentPart
        application={application}
        applicant={applicant}
        renderGuarantor={renderGuarantor}
      />
    </div>
  );
};

export default VacancyApplicantDetailedComponent;
